import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Snackbar,
    Card,
    CardContent,
    Typography,
    IconButton,
    Box,
    Chip,
    Tooltip
} from '@material-ui/core';
import {
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Info as InfoIcon,
    FileCopy as CopyIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { generalActions } from '../store/slices/generalSlice';
import { useTranslation } from 'react-i18next';
import { setGlobalNotificationCallback } from 'src/utils/notificationManager';

const useStyles = makeStyles((theme) => ({
    card: {
        backgroundColor: '#ffffff',
        color: '#333333',
        minWidth: 320,
        maxWidth: 480,
        width: '90vw',
        [theme.breakpoints.up('sm')]: {
            minWidth: 400,
            maxWidth: 500,
            width: 'auto',
        },
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        borderRadius: 12,
        border: '1px solid #e0e0e0',
        borderLeft: '4px solid #4CAF50',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1),
        paddingBottom: theme.spacing(0.5),
        borderBottom: '1px solid #f0f0f0',
        [theme.breakpoints.up('sm')]: {
            marginBottom: theme.spacing(1.5),
            paddingBottom: theme.spacing(1),
        },
    },
    successIcon: {
        fontSize: 20,
        marginRight: theme.spacing(0.5),
        color: '#4CAF50',
        [theme.breakpoints.up('sm')]: {
            fontSize: 24,
            marginRight: theme.spacing(1),
        },
    },
    title: {
        fontWeight: 600,
        color: '#333333',
        fontSize: '1rem',
        [theme.breakpoints.up('sm')]: {
            fontSize: '1.25rem',
        },
    },
    description: {
        color: '#666666',
        marginBottom: theme.spacing(1.5),
        fontSize: '0.85rem',
        [theme.breakpoints.up('sm')]: {
            marginBottom: theme.spacing(2),
            fontSize: '0.875rem',
        },
    },
    transactionSection: {
        backgroundColor: '#f8f9fa',
        borderRadius: 8,
        padding: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
        border: '1px solid #e9ecef',
        [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(1.5),
            marginBottom: theme.spacing(2),
        },
    },
    transactionLabel: {
        fontWeight: 600,
        color: '#333333',
        marginBottom: theme.spacing(0.5),
        fontSize: '0.8rem',
        [theme.breakpoints.up('sm')]: {
            fontSize: '0.875rem',
        },
    },
    transactionChip: {
        backgroundColor: '#e3f2fd',
        color: '#1976d2',
        fontFamily: 'Roboto',
        fontWeight: 'bold',
        cursor: 'pointer',
        fontSize: '0.7rem',
        height: 28,
        '&:hover': {
            backgroundColor: '#bbdefb',
        },
        [theme.breakpoints.up('sm')]: {
            fontSize: '0.8rem',
            height: 32,
        },
    }, infoSection: {
        backgroundColor: '#f0f4f8',
        borderRadius: 8,
        border: '1px solid #d1e7dd',
        marginTop: theme.spacing(1),
        overflow: 'hidden',
        transition: 'all 0.3s ease',
    },
    infoHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1),
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
        [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(1.2),
        },
    }, infoButton: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        fontSize: '0.8rem',
        color: '#17a2b8',
        fontWeight: 600,
        fontFamily: theme.typography.fontFamily,
        [theme.breakpoints.up('sm')]: {
            fontSize: '0.875rem',
        },
    },
    infoContent: {
        padding: `0 ${theme.spacing(1)}px ${theme.spacing(1)}px`,
        [theme.breakpoints.up('sm')]: {
            padding: `0 ${theme.spacing(1.2)}px ${theme.spacing(1.2)}px`,
        },
    },
    infoText: {
        fontSize: '0.75rem',
        lineHeight: 1.4,
        color: '#495057',
        [theme.breakpoints.up('sm')]: {
            fontSize: '0.875rem',
            lineHeight: 1.5,
        },
    },
    copyButton: {
        color: '#1976d2',
        padding: 2,
        '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
        },
        [theme.breakpoints.up('sm')]: {
            padding: 4,
        },
    },
    closeButton: {
        color: '#666666',
        padding: 2,
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        [theme.breakpoints.up('sm')]: {
            padding: 4,
        },
    },
}));

const NotificationSnackbar = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation('overview');
    const [lastTransactionId, setLastTransactionId] = useState(null);
    const [copied, setCopied] = useState(false);
    const [infoExpanded, setInfoExpanded] = useState(false);

    useEffect(() => {
        // Merkezi notification callback'ini set et
        setGlobalNotificationCallback((transactionId) => {
            setLastTransactionId(transactionId);
        });
    }, []);

    const handleClose = () => {
        setLastTransactionId(null);
        setCopied(false);
        setInfoExpanded(false);
    };

    const handleCopyTransactionId = async () => {
        try {
            await navigator.clipboard.writeText(lastTransactionId);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy transaction ID:', err);
        }
    };

    const toggleInfoExpanded = () => {
        setInfoExpanded(!infoExpanded);
    };

    return (
        <Snackbar
            open={Boolean(lastTransactionId)}
            autoHideDuration={8000}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
            <Card className={classes.card}>
                <CardContent>
                    <div className={classes.header}>                            <div style={{ display: 'flex', alignItems: 'center' }}>
                        <CheckCircleIcon className={classes.successIcon} />
                        <Typography variant="h6" className={classes.title}>
                            {t('transactionSuccessful')}
                        </Typography>
                    </div>
                        <IconButton onClick={handleClose} className={classes.closeButton}>
                            <CloseIcon />
                        </IconButton>
                    </div>                        <Typography variant="body2" className={classes.description}>
                        {t('operationCompleted')}
                    </Typography><div className={classes.transactionSection}>                            <Typography variant="subtitle2" className={classes.transactionLabel}>
                        {t('transactionId')}
                    </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                                label={lastTransactionId}
                                className={classes.transactionChip}
                                onClick={handleCopyTransactionId}
                            />
                            <Tooltip title={copied ? t('copied') : t('copyTransactionId')}>
                                <IconButton
                                    className={classes.copyButton}
                                    onClick={handleCopyTransactionId}
                                    size="small"
                                >
                                    <CopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </div>                        <div className={classes.infoSection}>
                        <div className={classes.infoHeader} onClick={toggleInfoExpanded}>                                <div className={classes.infoButton}>
                            <InfoIcon fontSize="small" />
                            <span>{t('whatIsTransactionId')}</span>
                        </div>
                            {infoExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </div>
                        {infoExpanded && (
                            <div className={classes.infoContent}>                                    <Typography variant="body2" className={classes.infoText}>
                                {t('transactionIdInfo')}
                            </Typography>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        </Snackbar>
    );
};

export default NotificationSnackbar;
